import { getPostPurchaseRedirectPath, SUBSCRIPTION_PARAM } from "common/helpers";
import { RoutesPath } from "flow-components/routes/routes";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * DSH-853: sends a buyer to their plan's page after Stripe hands the browser
 * back to the app.
 *
 * The destination is derived from the `?subscription=<slug>` param that the
 * checkout callers put in Stripe's successUrl — a synchronous value that is
 * already in the URL when the app boots. It deliberately does NOT read
 * ViewerContext.subscription: that arrives from the webhook over a websocket
 * (useInitViewer) and can land seconds later, so gating the redirect on it
 * would mean the redirect sometimes never happens.
 *
 * This covers the two checkout flows that come back with a full page load:
 * UpgradeSubscriptionModalV3 (opens Stripe in a new tab, no success callback —
 * the NEW tab is the one that returns here) and ProfileBilling's same-tab
 * checkout. The signup wizard's own result page is excluded: it owns this param
 * and has its own "You're all set!" step.
 *
 * The param is dropped on the way out so the redirect is one-shot. It is not
 * paired with a sessionStorage "just checked out" marker on purpose: Stripe is
 * opened in a new tab, and Chrome does not copy sessionStorage into a tab
 * opened on a cross-origin URL — the marker would be gone by the time the tab
 * comes back and the redirect would silently never fire. The trade-off is that
 * re-opening the checkout-return URL from browser history redirects once more.
 */
export const usePostPurchaseRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandled = useRef(false);

  useEffect(() => {
    // Mount-only: the param can only ever arrive through a full page load
    // coming back from Stripe, never through an in-app navigation.
    if (hasHandled.current) return;
    hasHandled.current = true;

    if (location.pathname === RoutesPath.StripeCheckoutResult) return;

    const productSlug = new URLSearchParams(location.search).get(
      SUBSCRIPTION_PARAM
    );
    const redirectPath = getPostPurchaseRedirectPath(productSlug);

    if (!redirectPath) return;

    // Replaces (not pushes) so Back does not bounce the buyer through the
    // checkout-return URL and trigger this again. Also runs when the buyer is
    // already on the destination, to clear the param from the address bar.
    navigate(redirectPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
